import { FC, PropsWithChildren } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import { baseUrl } from 'config';
import Head from 'next/head';
import Image from 'next/image';
import macbookImg from 'public/images/help/sherpas-help.png';

import NextLinkWithNoCss from '@/components/NextLinkWithNoCss/NextLinkWithNoCss';
import { Button } from '@/designSystem/Button/Button';
import Icon from '@/designSystem/Icon/Icon';
import Hidden from '@/generic/Hidden/Hidden';
import { useIsMobile } from '@/utils/useResponsive';

import messages from './messages';

import useStyles from './styles';

const HelpCenter: FC<PropsWithChildren<unknown>> = () => {
  const { classes, cx } = useStyles();
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();

  return (
    <>
      <Head>
        <title>Contact Les Sherpas : Support Client & Centre d’Aide</title>
        <meta
          name="description"
          content="▷ Une question, une réclamation relative à votre accompagnement ? L'équipe des Sherpas vous répond dans les plus brefs délais !"
        />
        <link rel="canonical" href={`${baseUrl}centre-aide`} />
      </Head>
      <div className={classes.root}>
        <div className={classes.mainContent}>
          <div>
            <Typography variant="titleLg" component="h1">
              <FormattedMessage {...messages.title} />
            </Typography>
          </div>
          <div className={classes.list}>
            <div className={cx(classes.helpLink, classes.topHelp)}>
              <div className={classes.helpLinkTitle}>
                <Icon icon="support" color="brandEmphasis" size="2xlarge" />
                <div className={classes.content}>
                  <Typography variant="bodyLgBold" component="span">
                    <FormattedMessage {...messages.faqTitle} />
                  </Typography>
                  <Typography
                    variant="bodyMd"
                    component="span"
                    className={classes.textSecondary}
                  >
                    <FormattedMessage {...messages.faqDescription} />
                  </Typography>
                </div>
              </div>
              <NextLinkWithNoCss
                href="https://les-sherpas.zendesk.com/hc/fr/"
                isReplace
                className={classes.link}
              >
                <Button
                  label={formatMessage(messages.faqButton)}
                  isFullWidth={isMobile}
                />
              </NextLinkWithNoCss>
            </div>
            <div className={cx(classes.helpLink, classes.bottomHelp)}>
              <div className={classes.helpLinkTitle}>
                <Icon icon="mail" color="brandEmphasis" size="2xlarge" />
                <div className={classes.content}>
                  <Typography variant="bodyLgBold" component="span">
                    <FormattedMessage {...messages.contactTitle} />
                  </Typography>
                  <Typography
                    variant="bodyMd"
                    component="span"
                    className={classes.textSecondary}
                  >
                    <FormattedMessage {...messages.contactDescription} />
                  </Typography>
                </div>
              </div>
              <NextLinkWithNoCss
                href="mailto:support@sherpas.com"
                isReplace
                className={classes.link}
              >
                <Button
                  variant="secondary"
                  label={formatMessage(messages.contactButton)}
                  isFullWidth={isMobile}
                />
              </NextLinkWithNoCss>
            </div>
          </div>
        </div>
        <Hidden variant="lg">
          <div className={classes.mainImage}>
            <div className={classes.image}>
              <div className={classes.gradientRectangle} />
              <Image
                src={macbookImg}
                alt="Donner des cours"
                sizes="100vw"
                fill
              />
            </div>
          </div>
        </Hidden>
      </div>
    </>
  );
};
export default HelpCenter;
